import { useEffect, useMemo, useRef } from 'react';
import { useFormikContext } from 'formik';
import { useRfiFormField, useFormManageFields } from '../../RfiFormProvider';
import { ISSUE_ATTRIBUTE_NAMES } from '../../../constants/issue-attribute-names';
import { RfiCreateFormValues } from '../../types';
import { PureRfiTypeField } from './PureRfiTypeField';
import { usePopulateRfiTypeFields, FieldSetters } from '../../../hooks/usePopulateRfiTypeFields';
import { clearHiddenFieldValues } from '../../helpers/clearHiddenFieldValues';

export const RfiType = () => {
  const formik = useFormikContext<RfiCreateFormValues>();
  const fieldName = ISSUE_ATTRIBUTE_NAMES.RFI_TYPE_ID;
  const { isFieldsMandatory, errorMessage, isSubmitting, fieldRef } = useRfiFormField(fieldName);
  const selectedRfiTypeId = formik.values[fieldName];

  const setRfiType = (id: string) => {
    formik.setFieldValue(fieldName, id);
  };

  const fieldSetters: FieldSetters = useMemo(() => {
    const getFieldSetter = (fieldName: string) => (value: unknown) =>
      formik.setFieldValue(fieldName, value);
    return {
      assignedTo: getFieldSetter(ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO),
      locationDescription: getFieldSetter(ISSUE_ATTRIBUTE_NAMES.LOCATION_DESCRIPTION),
      reference: getFieldSetter(ISSUE_ATTRIBUTE_NAMES.SUB_ID),
      costImpact: getFieldSetter(ISSUE_ATTRIBUTE_NAMES.COST),
      scheduleImpact: getFieldSetter(ISSUE_ATTRIBUTE_NAMES.SCHEDULE),
      priority: getFieldSetter(ISSUE_ATTRIBUTE_NAMES.PRIORITY),
      category: getFieldSetter(ISSUE_ATTRIBUTE_NAMES.CATEGORY),
      discipline: getFieldSetter(ISSUE_ATTRIBUTE_NAMES.DISCIPLINE),
      dueDateOffset: getFieldSetter(ISSUE_ATTRIBUTE_NAMES.DUE_DATE),
      watchers: getFieldSetter(ISSUE_ATTRIBUTE_NAMES.WATCHERS),
      coReviewers: getFieldSetter(ISSUE_ATTRIBUTE_NAMES.CO_REVIEWERS),
      customAttributes: getFieldSetter(ISSUE_ATTRIBUTE_NAMES.CUSTOM_ATTRIBUTES),
    };
  }, []);

  usePopulateRfiTypeFields({ selectedRfiTypeId, fieldSetters, formStatus: formik.values.status });

  // When the RFI type changes, reset any standard or custom field that the
  // new type marks as hidden so its value never sneaks into the payload, and
  // clear `touched` + `submitCount` so stale inline errors from the previous
  // schema do not bleed into the new UI. Skip the very first run for a given
  // type to avoid clobbering values set by `usePopulateRfiTypeFields`.
  //
  // Why the `formikRef`: the effect must read the *current* `formik.values`
  // and call `formik.setFieldValue/setTouched/setFormikState` without
  // re-firing on every keystroke. Capturing the whole context in a ref
  // (refreshed on every render) lets the dep array stay genuinely
  // exhaustive — only the rfi-type / FF / hidden-list inputs trigger the
  // effect.
  const { hiddenStandardNames, hiddenCustomIds, isFFEnabled } = useFormManageFields();
  const formikRef = useRef(formik);
  formikRef.current = formik;
  const lastRfiTypeIdRef = useRef<string | undefined>(selectedRfiTypeId);
  useEffect(() => {
    if (lastRfiTypeIdRef.current === selectedRfiTypeId) return;
    lastRfiTypeIdRef.current = selectedRfiTypeId;

    const currentFormik = formikRef.current;
    if (isFFEnabled) {
      clearHiddenFieldValues({
        hiddenStandardNames,
        hiddenCustomIds,
        values: currentFormik.values as Record<string, unknown> & {
          customAttributes?: Array<{ id: string; values: Array<string | null> }>;
        },
        // `clearHiddenFieldValues` types `setFieldValue` as `() => void`,
        // but `formik.setFieldValue` returns a Promise we don't need to
        // await here — wrap so the Promise is discarded into a void return.
        setFieldValue: (field, value, shouldValidate) => {
          currentFormik.setFieldValue(field, value, shouldValidate);
        },
      });
    }

    // Single `setFormikState` so React batches the `touched` and `submitCount`
    // resets into one re-render — calling `setTouched` and `setFormikState`
    // separately inside a `useEffect` (outside React's event-handler batch
    // scope on React 17) would otherwise fire two sequential renders.
    currentFormik.setFormikState((state) => ({ ...state, touched: {}, submitCount: 0 }));
  }, [selectedRfiTypeId, isFFEnabled, hiddenStandardNames, hiddenCustomIds]);

  return (
    <PureRfiTypeField
      ref={fieldRef}
      required={isFieldsMandatory}
      errorMessage={errorMessage}
      selectedRfiTypeId={selectedRfiTypeId}
      setRfiType={setRfiType}
      showDiagrams={false}
      disabled={isSubmitting}
    />
  );
};
